import { type TypedDocumentNode, useApolloClient } from '@apollo/client';
import { productDescription } from '@shared/core/product';
import { useMemo } from 'react';
import { EXPORT_PAGE_SIZE, EXPORT_ROW_CEILING, exportCeilingMessage } from 'src/modules/grids';
import type { CommodityCatalogRootQuery, CommodityCatalogRootQueryVariables } from 'types/graphql';
import type {
  CatalogCommodityRow,
  CatalogProductRow,
  CatalogScope,
  CatalogSortItem,
  CatalogTransport,
} from '../catalogDataSource';
import { commodityGroupKey } from '../catalogDataSource';
import { COMMODITY_CATALOG_PRODUCTS_PAGE_QUERY, type CommodityCatalogProduct } from './useCommoditiesPage';

/** The catalog ROOT: commodity headers with the count of products matching the current scope. */
export const COMMODITY_CATALOG_ROOT_QUERY: TypedDocumentNode<
  CommodityCatalogRootQuery,
  CommodityCatalogRootQueryVariables
> = gql`
  query CommodityCatalogRootQuery($input: StandardPageInput) {
    commodityCatalogPage(input: $input) {
      totalCount
      pageInfo {
        hasNextPage
        endCursor
      }
      edges {
        cursor
        node {
          id
          name
          matchingProductCount
        }
      }
    }
  }
`;

type RawPage<TNode> = {
  totalCount?: number | null;
  pageInfo?: { hasNextPage?: boolean | null; endCursor?: string | null } | null;
  edges?: Array<{ cursor: string; node: TNode } | null> | null;
} | null;

const nodesOf = <TNode>(page: RawPage<TNode>): TNode[] =>
  (page?.edges ?? []).flatMap((edge) => (edge?.node ? [edge.node] : []));

const pageFlags = (page: RawPage<unknown>) => ({
  totalCount: page?.totalCount ?? null,
  hasNextPage: page?.pageInfo?.hasNextPage === true,
  endCursor: page?.pageInfo?.endCursor ?? undefined,
});

/** `commodityId` rides in as a grid filter — the pagination extension turns it into a Prisma
 * predicate under the SAME tenant and soft-delete clauses as every other catalog read. */
const commodityScopeFilter = (commodityId: number) => ({ field: 'commodityId', where: [{ equals: commodityId }] });

const toCatalogProductRow = (product: CommodityCatalogProduct): CatalogProductRow => ({
  ...product,
  description: productDescription({
    ...product,
    organic: product.organic ?? undefined,
    countryOfOrigin: product.countryOfOrigin ?? null,
  }),
});

/** The variables of one commodity window; the route loader preloads the first one with them. */
export function catalogRootVariables({
  first,
  after,
  scope,
  sortItems,
}: {
  first: number;
  after?: string;
  scope: CatalogScope;
  sortItems?: CatalogSortItem[];
}): CommodityCatalogRootQueryVariables {
  return {
    input: {
      first,
      after,
      searchText: scope.searchText,
      gridFilters: scope.gridFilters,
      quickFilters: scope.quickFilters,
      ...(sortItems ? { sortItems } : {}),
    },
  };
}

export function catalogRootPage(data: CommodityCatalogRootQuery | undefined) {
  const page = data?.commodityCatalogPage ?? null;
  const rows: CatalogCommodityRow[] = nodesOf(page).map((node) => ({
    id: commodityGroupKey(node.id),
    commodityId: node.id,
    name: node.name,
    matchingProductCount: node.matchingProductCount,
  }));
  return { rows, ...pageFlags(page) };
}

export type CatalogClient = CatalogTransport & {
  fetchAllProducts(args: { scope: CatalogScope; commodityId?: number }): Promise<CatalogProductRow[]>;
};

export function useCatalogTransport(): CatalogClient {
  const client = useApolloClient();

  return useMemo<CatalogClient>(() => {
    async function productPage(args: {
      first: number;
      after?: string;
      commodityId?: number;
      scope: CatalogScope;
      sortItems?: CatalogSortItem[];
    }) {
      const { data } = await client.query({
        query: COMMODITY_CATALOG_PRODUCTS_PAGE_QUERY,
        variables: {
          input: {
            first: args.first,
            after: args.after,
            searchText: args.scope.searchText,
            quickFilters: args.scope.quickFilters,
            sortItems: args.sortItems?.length ? args.sortItems : undefined,
            gridFilters:
              args.commodityId === undefined
                ? args.scope.gridFilters
                : [...args.scope.gridFilters, commodityScopeFilter(args.commodityId)],
          },
        },
        fetchPolicy: 'network-only',
      });
      const page = data?.productPage as RawPage<CommodityCatalogProduct>;
      return { rows: nodesOf(page).map(toCatalogProductRow), ...pageFlags(page) };
    }

    return {
      async fetchCommodityPage(args) {
        const { data } = await client.query({
          query: COMMODITY_CATALOG_ROOT_QUERY,
          variables: catalogRootVariables(args),
          fetchPolicy: 'network-only',
        });
        return catalogRootPage(data);
      },

      fetchProductPage: (args) => productPage(args),

      async fetchAllProducts({ scope, commodityId }) {
        const rows: CatalogProductRow[] = [];
        let after: string | undefined;
        do {
          const page = await productPage({ first: EXPORT_PAGE_SIZE, after, commodityId, scope });
          rows.push(...page.rows);
          if (rows.length > EXPORT_ROW_CEILING) throw new Error(exportCeilingMessage(EXPORT_ROW_CEILING));
          after = page.hasNextPage ? page.endCursor : undefined;
        } while (after);
        return rows;
      },
    };
  }, [client]);
}
